import React, { useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ShoppingBag, RefreshCw, Search, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

// NOWPayments payment_status → colored pill
const paymentStatusStyle = (s) => {
  if (!s) return { cls: "bg-white/5 text-[#8a8a8a] border-white/10", label: "Awaiting" };
  const map = {
    finished: { cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", label: "Finished" },
    confirmed: { cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", label: "Confirmed" },
    confirming: { cls: "bg-blue-500/15 text-blue-400 border-blue-500/30", label: "Confirming" },
    sending: { cls: "bg-blue-500/15 text-blue-400 border-blue-500/30", label: "Sending" },
    partially_paid: { cls: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30", label: "Partial" },
    waiting: { cls: "bg-[#ff7a00]/15 text-[#ff9a2f] border-[#ff7a00]/30", label: "Waiting" },
    failed: { cls: "bg-red-500/15 text-red-400 border-red-500/30", label: "Failed" },
    expired: { cls: "bg-red-500/15 text-red-400 border-red-500/30", label: "Expired" },
    refunded: { cls: "bg-red-500/15 text-red-400 border-red-500/30", label: "Refunded" },
  };
  return map[s] || { cls: "bg-white/5 text-[#cfcfcf] border-white/10", label: s };
};

export const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [syncing, setSyncing] = useState({});
  const [syncingAll, setSyncingAll] = useState(false);

  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data || []));
  useEffect(() => { load(); }, []);

  const syncOne = async (id) => {
    setSyncing((s) => ({ ...s, [id]: true }));
    try {
      const { data } = await api.post(`/admin/orders/${id}/sync-payment`);
      setOrders((prev) => prev.map((o) => (o.id === id ? data : o)));
      toast.success(`Synced: ${data.payment_status || "no payment yet"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing((s) => ({ ...s, [id]: false }));
    }
  };

  const syncPending = async () => {
    const pending = orders.filter((o) => o.status === "pending" || ["waiting", "confirming", "sending", "partially_paid"].includes(o.payment_status));
    if (!pending.length) { toast.info("No pending orders to sync"); return; }
    setSyncingAll(true);
    try {
      const results = await Promise.allSettled(pending.map((o) => api.post(`/admin/orders/${o.id}/sync-payment`)));
      const ok = results.filter((r) => r.status === "fulfilled").length;
      toast.success(`Synced ${ok}/${pending.length} orders`);
      await load();
    } finally {
      setSyncingAll(false);
    }
  };

  const filtered = orders.filter((o) => {
    if (!q.trim()) return true;
    const k = q.trim().toLowerCase();
    return (
      (o.user_email || "").toLowerCase().includes(k) ||
      (o.user_name || "").toLowerCase().includes(k) ||
      (o.challenge_name || "").toLowerCase().includes(k) ||
      (o.id || "").toLowerCase().includes(k)
    );
  });

  return (
    <div className="space-y-6" data-testid="admin-orders-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Orders</h1>
          <p className="text-[#8a8a8a] text-sm mt-1">All challenge purchases with live payment status.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a8a8a]" size={16} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email, name, challenge..." className="pl-9 bg-[#0a0a0a] border-[#ff7a00]/20 h-11" data-testid="admin-orders-search" />
          </div>
          <Button onClick={syncPending} disabled={syncingAll} variant="outline" className="border-[#ff7a00]/30 text-[#ff9a2f] hover:bg-[#ff7a00]/10 hover:text-[#ff7a00] h-11" data-testid="admin-orders-sync-all">
            <RefreshCw size={14} className={`mr-2 ${syncingAll ? "animate-spin" : ""}`} />
            Sync Pending
          </Button>
        </div>
      </div>

      <div className="glass rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] tracking-widest text-[#8a8a8a] uppercase border-b border-[#ff7a00]/15">
              <th className="text-left p-4">Date</th>
              <th className="text-left p-4">Trader</th>
              <th className="text-left p-4">Challenge</th>
              <th className="text-right p-4">Size</th>
              <th className="text-right p-4">Paid</th>
              <th className="text-left p-4">Pay</th>
              <th className="text-left p-4">Payment</th>
              <th className="text-left p-4">Order</th>
              <th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-[#8a8a8a]"><ShoppingBag className="mx-auto text-[#ff7a00] mb-3" size={28} />{orders.length === 0 ? "No orders yet." : "No orders match your search."}</td></tr>
            )}
            {filtered.map((o) => {
              const ps = paymentStatusStyle(o.payment_status);
              return (
                <tr key={o.id} className="border-b border-[#ff7a00]/5 hover:bg-white/2 align-top" data-testid={`admin-order-${o.id}`}>
                  <td className="p-4 text-[#8a8a8a] whitespace-nowrap">
                    <div>{new Date(o.created_at).toLocaleDateString()}</div>
                    <div className="text-[10px] text-[#666] mt-0.5">{new Date(o.created_at).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}</div>
                  </td>
                  <td className="p-4">
                    <div className="text-[#f5f5f5] font-medium" data-testid={`order-email-${o.id}`}>{o.user_email || "—"}</div>
                    {o.user_name && <div className="text-[10px] text-[#8a8a8a] mt-0.5">{o.user_name}</div>}
                  </td>
                  <td className="p-4 text-[#f5f5f5]">{o.challenge_name}</td>
                  <td className="p-4 text-right text-[#cfcfcf]">${o.account_size.toLocaleString()}</td>
                  <td className="p-4 text-right font-display font-bold text-[#ff7a00]">${o.price_amount}</td>
                  <td className="p-4 text-[#cfcfcf] uppercase text-[11px]">{o.pay_currency || "—"}</td>
                  <td className="p-4">
                    <Badge className={`uppercase text-[10px] tracking-widest ${ps.cls}`} data-testid={`order-payment-status-${o.id}`}>{ps.label}</Badge>
                    {o.last_synced_at && <div className="text-[9px] text-[#666] mt-1">synced {new Date(o.last_synced_at).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}</div>}
                  </td>
                  <td className="p-4">
                    <Badge className={`uppercase text-[10px] tracking-widest ${
                      o.status === "paid" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" :
                      o.status === "assigned" ? "bg-blue-500/15 text-blue-400 border-blue-500/30" :
                      o.status === "pending" ? "bg-[#ff7a00]/15 text-[#ff9a2f] border-[#ff7a00]/30" :
                      "bg-red-500/15 text-red-400 border-red-500/30"
                    }`} data-testid={`order-status-${o.id}`}>{o.status}</Badge>
                  </td>
                  <td className="p-4 text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => syncOne(o.id)}
                      disabled={syncing[o.id]}
                      className="text-[#cfcfcf] hover:text-[#ff7a00] hover:bg-[#ff7a00]/10 h-8 px-2"
                      data-testid={`order-sync-${o.id}`}
                    >
                      <RefreshCw size={13} className={syncing[o.id] ? "animate-spin" : ""} />
                    </Button>
                    {o.invoice_url && (
                      <a href={o.invoice_url} target="_blank" rel="noreferrer" className="inline-block ml-1" data-testid={`order-invoice-link-${o.id}`}>
                        <Button size="sm" variant="ghost" className="text-[#cfcfcf] hover:text-[#ff7a00] hover:bg-[#ff7a00]/10 h-8 px-2">
                          <ExternalLink size={13} />
                        </Button>
                      </a>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
